// Utility to convert number to words (INR)
export const numberToWords = (num) => {
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000)
      return `${a[Math.floor(n / 100)]} Hundred${n % 100 ? " " + inWords(n % 100) : ""
        }`;
    if (n < 100000)
      return `${inWords(Math.floor(n / 1000))} Thousand${n % 1000 ? " " + inWords(n % 1000) : ""
        }`;
    return "";
  };
  if (num == 0) return "Zero";

  return inWords(Number(num));
};

export const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const roundCurrency = (value) => {
  const num = toNumber(value);
  return Number(num.toFixed(2));
};

/**
 * Format unit price to preserve precision (up to maxDecimals, default 4)
 * without unnecessary trailing zeros beyond 2 decimal places.
 * E.g., 0.857 -> "0.857", 3.145 -> "3.145", 0.4 -> "0.40", 12 -> "12.00".
 */
export const formatPrice = (val, maxDecimals = 4) => {
  if (val === null || val === undefined || isNaN(val)) return '-';
  const num = Number(val);
  const fixed = num.toFixed(maxDecimals);
  const [intPart, decPart] = fixed.split('.');
  if (!decPart) return `${intPart}.00`;
  let trimmedDec = decPart.replace(/0+$/, '');
  if (trimmedDec.length < 2) {
    trimmedDec = trimmedDec.padEnd(2, '0');
  }
  return `${intPart}.${trimmedDec}`;
};

/** Round pharmacy invoice totals down to the nearest whole rupee. */
export const calculatePharmacyInvoiceRounding = (netTotal) => {
  const net = roundCurrency(netTotal);
  const grandTotal = Math.floor(net + 1e-9);
  const roundOff = roundCurrency(grandTotal - net);
  return {
    netTotal: net,
    grandTotal,
    roundOff,
    roundOffDisplay: Math.abs(roundOff),
    isPositiveRoundOff: roundOff > 0
  };
};

/** Derive pharmacy grand total / round-off from the line-sum net amount. */
export const resolvePharmacyInvoiceTotals = ({ netTotal } = {}) =>
  calculatePharmacyInvoiceRounding(netTotal);

/** Scale GST breakdown so taxable + tax equals the post-round-off grand total. */
export const adjustGstSummaryForRoundOff = (gstSummary, grandTotal) => {
  if (!gstSummary) return null;

  const net = roundCurrency(gstSummary.totalAmount ?? 0);
  const roundedGrand = roundCurrency(grandTotal);

  if (!Number.isFinite(net) || net <= 0) {
    return {
      ...gstSummary,
      taxableValue: roundedGrand,
      totalAmount: roundedGrand,
      totalTax: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0
    };
  }

  if (Math.abs(net - roundedGrand) < 0.005) {
    return gstSummary;
  }

  const preTax = roundCurrency(gstSummary.totalTax ?? 0);
  if (preTax <= 0) {
    return {
      ...gstSummary,
      taxableValue: roundedGrand,
      totalTax: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      totalAmount: roundedGrand
    };
  }

  const ratio = roundedGrand / net;
  const totalTax = roundCurrency(preTax * ratio);
  const taxableValue = roundCurrency(roundedGrand - totalTax);

  if (gstSummary.mode === 'igst') {
    return {
      ...gstSummary,
      taxableValue,
      totalTax,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: totalTax,
      totalAmount: roundedGrand
    };
  }

  const cgstAmount = roundCurrency(totalTax / 2);
  const sgstAmount = roundCurrency(totalTax - cgstAmount);

  return {
    ...gstSummary,
    taxableValue,
    totalTax,
    cgstAmount,
    sgstAmount,
    igstAmount: 0,
    totalAmount: roundedGrand
  };
};

export const getPharmacyActiveItems = (invoiceRow) => {
  const pharmacyItems = Array.isArray(invoiceRow?.items?.pharmacy) ? invoiceRow.items.pharmacy : [];
  if (!pharmacyItems.length) return [];

  const normalizeItem = (item) => ({
    ...item,
    selling_price: toNumber(item?.selling_price ?? item?.price),
    quantity: toNumber(item?.quantity ?? item?.qty)
  });

  const hasStatusField = pharmacyItems.some((item) => item?.status);
  const hasCancelledItems = pharmacyItems.some((item) => item?.status === 'cancelled');

  if (!hasStatusField) {
    return pharmacyItems.map(normalizeItem).filter((item) => item.quantity > 0);
  }

  if (!hasCancelledItems) {
    return pharmacyItems
      .filter((item) => item?.status === 'active' || !item?.status)
      .map(normalizeItem)
      .filter((item) => item.quantity > 0);
  }

  const grouped = {};
  pharmacyItems.forEach((rawItem, index) => {
    const item = normalizeItem(rawItem);
    const itemKey = item?.id || item?.medicine_id || item?.non_medicine_item_id || `${item?.name || 'item'}-${index}`;

    if (!grouped[itemKey]) {
      grouped[itemKey] = {
        ...item,
        activeQuantity: 0,
        cancelledQuantity: 0
      };
    }

    if (item?.status === 'active') {
      grouped[itemKey].activeQuantity = item.quantity;
    } else if (item?.status === 'cancelled') {
      grouped[itemKey].cancelledQuantity += item.quantity;
    }
  });

  return Object.values(grouped)
    .map((item) => ({
      ...item,
      quantity: Math.max(item.activeQuantity - item.cancelledQuantity, 0)
    }))
    .filter((item) => item.quantity > 0);
};

export const getLabActiveItems = (invoiceRow) => {
  const labItems = Array.isArray(invoiceRow?.items?.lab_tests)
    ? invoiceRow.items.lab_tests
    : Array.isArray(invoiceRow?.items?.opd_appointment)
      ? invoiceRow.items.opd_appointment
      : [];

  if (!labItems.length) return [];
  return labItems.filter((item) => !item?.status || item?.status === 'active');
};

export const getInvoiceTotals = (invoiceRow, variant) => {
  const backendTotal = roundCurrency(invoiceRow?.total_amount);
  const roundOff = toNumber(invoiceRow?.round_off);

  if (variant === 'pharmacy') {
    const activeItems = getPharmacyActiveItems(invoiceRow);
    if (!activeItems.length) {
      return {
        subtotal: 0,
        totalDiscount: 0,
        roundOff,
        finalTotal: backendTotal,
        activeItems,
        isPositiveRoundOff: roundOff > 0
      };
    }

    const subtotal = activeItems.reduce(
      (sum, item) => sum + (toNumber(item?.selling_price ?? item?.price) * toNumber(item?.quantity ?? item?.qty)),
      0
    );
    const totalDiscount = activeItems.reduce((sum, item) => {
      const gross = toNumber(item?.selling_price ?? item?.price) * toNumber(item?.quantity ?? item?.qty);
      return sum + (gross * toNumber(item?.discount_in_perc) / 100);
    }, 0);

    const netFromLines = activeItems.reduce((sum, item) => {
      const gross = toNumber(item?.selling_price ?? item?.price) * toNumber(item?.quantity ?? item?.qty);
      return sum + roundCurrency(gross - gross * toNumber(item?.discount_in_perc) / 100);
    }, 0);
    const resolved = resolvePharmacyInvoiceTotals({ netTotal: netFromLines });

    return {
      subtotal,
      totalDiscount,
      roundOff: resolved.roundOff,
      finalTotal: resolved.grandTotal,
      activeItems,
      isPositiveRoundOff: resolved.isPositiveRoundOff
    };
  }

  if (variant === 'lab') {
    const activeItems = getLabActiveItems(invoiceRow);
    if (!activeItems.length) {
      return {
        subtotal: 0,
        totalDiscount: 0,
        roundOff,
        finalTotal: backendTotal,
        activeItems,
        isPositiveRoundOff: roundOff > 0
      };
    }

    const subtotal = activeItems.reduce((sum, item) => {
      const quantity = toNumber(item?.quantity) || 1;
      const unitPrice = toNumber(item?.manual_price) > 0
        ? toNumber(item?.manual_price)
        : toNumber(item?.selling_price ?? item?.price);
      return sum + (unitPrice * quantity);
    }, 0);

    const totalDiscount = activeItems.reduce((sum, item) => {
      const quantity = toNumber(item?.quantity) || 1;
      const unitPrice = toNumber(item?.manual_price) > 0
        ? toNumber(item?.manual_price)
        : toNumber(item?.selling_price ?? item?.price);
      const gross = unitPrice * quantity;
      return sum + (gross * toNumber(item?.discount_in_perc) / 100);
    }, 0);

    const finalTotal = subtotal - totalDiscount + roundOff;
    return {
      subtotal,
      totalDiscount,
      roundOff,
      finalTotal: Number.isFinite(finalTotal) ? roundCurrency(finalTotal) : backendTotal,
      activeItems,
      isPositiveRoundOff: roundOff > 0
    };
  }

  return {
    subtotal: 0,
    totalDiscount: 0,
    roundOff,
    finalTotal: backendTotal,
    activeItems: [],
    isPositiveRoundOff: roundOff > 0
  };
};

export const recalculateInvoiceTotal = (invoiceRow, variant) => getInvoiceTotals(invoiceRow, variant).finalTotal;
